import { useEffect, useState } from 'react';
import { usePopup } from '../../../providers/PopupProvider';
import { AuctionDto } from '../../../services/Auction/auctionService.dto';
import { itemService } from '../../../services/Item/itemService';
import PlaceBidPopup from '../components/PlaceBidPopup';
import { extractItemPrice, isAuctionExpired } from '../utils/auctionUtils';
import { useGlobalData } from '../../../providers/GlobalDataProvider';
import { usePlaceBidErrors } from './usePlaceBidErrors';
import { useAuctionEndDetection } from './useAuctionEndDetection';

export const usePlaceBidPopup = (auction?: AuctionDto) => {
  const { showPopover, hidePopover } = usePopup();
  const { paymentConfig } = useGlobalData();
  const { amountTooLow, auctionExpired, amountOverLimit, unknownError } = usePlaceBidErrors();
  const [canPlaceBid, setCanPlaceBid] = useState(false);

  useAuctionEndDetection(auction, () => setCanPlaceBid(false));

  useEffect(() => {
    setCanPlaceBid(deriveCanPlaceBid(auction));
  }, [auction]);

  const placeBid = (price: number, authToken: string, refetchAuction: () => void) => {
    itemService
      .addBid(auction!!.id, price, authToken)
      .catch(error => {
        if (error.response?.status === 400) {
          amountTooLow();
        } else if (error.response?.status === 409) {
          auctionExpired();
        } else if (error.response?.status === 422) {
          amountOverLimit();
        } else {
          unknownError();
        }
      })
      .finally(refetchAuction);
  };

  const showPlaceBidPopup = (auction: AuctionDto, authToken: string, refetchAuction: () => void) => {
    if (!canPlaceBid) return;

    const { currentItemPrice, minBidPrice } = extractItemPrice(auction);

    showPopover(
      <PlaceBidPopup
        currentItemPrice={currentItemPrice}
        minBidPrice={minBidPrice ? minBidPrice : 0}
        maxBidPrice={paymentConfig?.maxItemPrice}
        onPlaceBid={price => {
          hidePopover();
          placeBid(price, authToken, refetchAuction);
        }}
      />
    );
  };

  return { canPlaceBid, showPlaceBidPopup };
};

const deriveCanPlaceBid = (auction?: AuctionDto) => !!auction && !isAuctionExpired(auction);
